import { useEffect, useState } from 'react';

// Define SCSS file paths as constants (Use camelCase keys)
const SCSS_PATHS = {
  desktop: '../styles/SignInUpPage.scss',
  mobilePortrait: '../styles/SignInUpPage.mobile.portrait.scss',
  mobileLandscape: '../styles/SignInUpPage.mobile.landscape.scss',
  tabletPortrait: '../styles/SignInUpPage.tablet.portrait.scss',
  tabletLandscape: '../styles/SignInUpPage.tablet.landscape.scss',
};

// Define possible device types (Must match SCSS_PATHS keys)
type DeviceType =
  | 'mobilePortrait'
  | 'mobileLandscape'
  | 'tabletPortrait'
  | 'tabletLandscape'
  | 'desktop';

// Full device mapping (Use camelCase for consistency)
const deviceProfiles: {
  type: DeviceType;
  userAgent: RegExp;
  width: number[];
  height: number[];
}[] = [
  // ✅ EXISTING DEVICES (UNCHANGED)
  {
    type: 'mobilePortrait',
    userAgent: /iphone|android.*mobile/,
    width: [375],
    height: [667],
  },
  {
    type: 'mobilePortrait',
    userAgent: /iphone x|iphone xr|iphone 11/,
    width: [414],
    height: [896],
  },
  {
    type: 'mobilePortrait',
    userAgent: /iphone 5|iphone se/,
    width: [320],
    height: [568],
  },
  {
    type: 'mobilePortrait',
    userAgent: /samsung galaxy s[3-9]/,
    width: [360],
    height: [640],
  },
  {
    type: 'mobilePortrait',
    userAgent: /pixel 2|pixel 2 xl/,
    width: [411],
    height: [731],
  },
  { type: 'mobilePortrait', userAgent: /pixel 7/, width: [412], height: [915] },
  {
    type: 'tabletPortrait',
    userAgent: /ipad|tablet/,
    width: [768],
    height: [1024],
  },
  {
    type: 'tabletLandscape',
    userAgent: /ipad|tablet/,
    width: [1024],
    height: [768],
  },
  {
    type: 'mobileLandscape',
    userAgent: /iphone|android.*mobile/,
    width: [667],
    height: [375],
  },
  {
    type: 'mobileLandscape',
    userAgent: /iphone x|iphone xr|iphone 11/,
    width: [896],
    height: [414],
  },
  {
    type: 'mobileLandscape',
    userAgent: /samsung galaxy s[3-9]/,
    width: [640],
    height: [360],
  },
  {
    type: 'desktop',
    userAgent: /macintosh|windows|linux/,
    width: [1200],
    height: [800],
  },

  // ✅ FIXED: iPhone 12, 13, 14, 15 (Portrait & Landscape)
  {
    type: 'mobilePortrait',
    userAgent: /iphone (12|13|14|15|pro|max|mini)/,
    width: [390, 393, 428, 430],
    height: [780, 844, 852, 926, 932],
  },
  {
    type: 'mobileLandscape',
    userAgent: /iphone (12|13|14|15|pro|max|mini)/,
    width: [780, 844, 852, 926, 932],
    height: [360, 390, 393, 428, 430],
  },
];

// Function to determine and attach the correct SCSS file based on device type
const attachStylesheet = (deviceType: DeviceType) => {
  console.log(`📌 [getAgent] Applying styles for: ${deviceType}`);

  // ✅ Explicitly remove `SignInUpPage.scss` if switching away from desktop
  document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    const stylesheet = link as HTMLLinkElement;
    if (stylesheet.href.includes('SignInUpPage.scss')) {
      console.log(
        `🗑️ [getAgent] Removing desktop stylesheet: ${stylesheet.href}`
      );
      stylesheet.remove();
    }
  });

  // Ensure key exists in SCSS_PATHS
  const path = SCSS_PATHS[deviceType] || SCSS_PATHS.desktop;

  // ✅ Dynamically add the new stylesheet
  const linkTag = document.createElement('link');
  linkTag.rel = 'stylesheet';
  linkTag.href = path;
  document.head.appendChild(linkTag);

  console.log(`✅ [getAgent] Successfully attached stylesheet: ${path}`);
};



// Function to find the closest matching screen size
const findClosestMatch = (
  screenWidth: number,
  screenHeight: number
): DeviceType => {
  let closestMatch: DeviceType = 'mobilePortrait'; // ✅ Default to Mobile First
  let closestDifference = Infinity;

  deviceProfiles.forEach((profile) => {
    if (profile.type === 'desktop') return; // ✅ Ignore desktop unless no other match

    profile.width.forEach((w, i) => {
      const h = profile.height[i];
      const diff = Math.abs(w - screenWidth) + Math.abs(h - screenHeight);

      if (diff < closestDifference) {
        closestDifference = diff;
        closestMatch = profile.type;
      }
    });
  });

  console.log(
    `📌 [getAgent] No exact match found. Using closest size: ${closestMatch}`
  );
  return closestMatch;
};

// Custom Hook to determine device type and apply the correct styles
const useDeviceType = (): DeviceType => {
  const [deviceType, setDeviceType] = useState<DeviceType>('desktop');

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isLandscape = screenWidth > screenHeight;

    console.log('🔍 [getAgent] Detected User-Agent:', userAgent);
    console.log(
      `📏 [getAgent] Screen Dimensions: ${screenWidth}x${screenHeight}`
    );
    console.log(
      `📐 [getAgent] Orientation: ${isLandscape ? 'Landscape' : 'Portrait'}`
    );

    // ✅ FIXED: Match screen size along with user agent
    let matchedProfile = deviceProfiles.find(
      (profile) =>
        profile.userAgent.test(userAgent) &&
        profile.width.includes(screenWidth) &&
        profile.height.includes(screenHeight)
    );

    let selectedType: DeviceType;

    if (matchedProfile) {
      console.log(`✅ [getAgent] Matched Device: ${matchedProfile.userAgent}`);
      selectedType = isLandscape ? 'mobileLandscape' : 'mobilePortrait';
    } else {
      console.log(
        '❌ [getAgent] No Exact Match Found - Finding Closest Match...'
      );
      selectedType = findClosestMatch(screenWidth, screenHeight);
    }

    console.log(`📌 [getAgent] Final Device Type Assigned: ${selectedType}`);
    setDeviceType(selectedType);

    // Attach the correct stylesheet dynamically
    attachStylesheet(selectedType);
  }, []);

  return deviceType;
};

export default useDeviceType;
